import React from "react";
import styled from "styled-components";
import { useTranslate } from '@tolgee/react';
import { Volume2 } from "lucide-react";
import ControlButton from "./ControlButton";
import { useGenericComponentsStateService } from "../../Context/GenericComponentsStateContext";

import { ReactComponent as TranslateActive } from "../../Assets/iconsv2/controlButtons/TranslateActive.svg";
import { ReactComponent as TranslateInactive } from "../../Assets/iconsv2/controlButtons/TranslateInactive.svg";
import { ReactComponent as TranslateActiveLight } from "../../Assets/iconsv2/controlButtons/TranslateActiveLight.svg";
import { ReactComponent as TranslateInactiveLight } from "../../Assets/iconsv2/controlButtons/TranslateInactiveLight.svg";

import { ReactComponent as ChatIconActive } from "../../Assets/iconsv2/controlButtons/ChatsActive.svg";
import { ReactComponent as ChatIconInactive } from "../../Assets/iconsv2/controlButtons/ChatsInactive.svg";
import { ReactComponent as ChatIconActiveLight } from "../../Assets/iconsv2/controlButtons/ChatsActiveLight.svg";
import { ReactComponent as ChatIconInactiveLight } from "../../Assets/iconsv2/controlButtons/ChatsInactiveLight.svg";

import { ReactComponent as PollIconActive } from "../../Assets/iconsv2/controlButtons/PollBarsActive.svg";
import { ReactComponent as PollIconInactive } from "../../Assets/iconsv2/controlButtons/PollBarsInactive.svg";
import { ReactComponent as PollIconActiveLight } from "../../Assets/iconsv2/controlButtons/PollBarsActiveLight.svg";
import { ReactComponent as PollIconInactiveLight } from "../../Assets/iconsv2/controlButtons/PollBarsInactiveLight.svg";

import { ReactComponent as CogwheelIcon } from "../../Assets/iconsv2/controlButtons/cogwheel.svg";
import { ReactComponent as CogwheelIconLight } from "../../Assets/iconsv2/controlButtons/cogwheelLight.svg";
import { ReactComponent as CloseCall } from "../../Assets/iconsv2/controlButtons/closeCall.svg";

const VolumeTabIcon = ({ $active }) => (
  <VolumeIconCircle $active={$active}>
    <Volume2 size={16} />
  </VolumeIconCircle>
);

const MobileTabBar = ({
  activeTab,
  onSelectTab,
  chatAllowed,
  pollsAllowed,
  translationActive,
  unreadChatCount = 0,
  activePollBadge = false,
  onOpenSettings,
  onDisconnect,
  showDisconnect = true,
}) => {
  const { theme } = useGenericComponentsStateService();
  const { t } = useTranslate();
  const isLight = theme === "light";

  return (
    <TabBarContainer>
      {translationActive && (
        <ControlButton
          active={activeTab === "feed"}
          activeIcon={isLight ? <TranslateActiveLight /> : <TranslateActive />}
          inactiveIcon={isLight ? <TranslateInactiveLight /> : <TranslateInactive />}
          label={t('conferences.live_feed') || "Live Feed"}
          onClick={() => onSelectTab("feed")}
        />
      )}

      {chatAllowed && (
        <TabButtonWrapper>
          <ControlButton
            active={activeTab === "chat"}
            activeIcon={isLight ? <ChatIconActiveLight /> : <ChatIconActive />}
            inactiveIcon={isLight ? <ChatIconInactiveLight /> : <ChatIconInactive />}
            label={t('call_modal.chat_option')}
            onClick={() => onSelectTab("chat")}
          />
          {unreadChatCount > 0 && activeTab !== "chat" && <TabBadge />}
        </TabButtonWrapper>
      )}

      {pollsAllowed && (
        <TabButtonWrapper>
          <ControlButton
            active={activeTab === "polls"}
            activeIcon={isLight ? <PollIconActiveLight /> : <PollIconActive />}
            inactiveIcon={isLight ? <PollIconInactiveLight /> : <PollIconInactive />}
            label={t('conferences.polls')}
            onClick={() => onSelectTab("polls")}
          />
          {activePollBadge && activeTab !== "polls" && <TabBadge />}
        </TabButtonWrapper>
      )}

      {translationActive && (
        <ControlButton
          active={activeTab === "audio"}
          activeIcon={<VolumeTabIcon $active />}
          inactiveIcon={<VolumeTabIcon $active={false} />}
          label={t('live_feed.voice_label')}
          onClick={() => onSelectTab("audio")}
        />
      )}

      <ControlButton
        active={true}
        activeIcon={isLight ? <CogwheelIconLight /> : <CogwheelIcon />}
        label={t('general.settings')}
        onClick={onOpenSettings}
      />

      {showDisconnect && (
        <ControlButton
          active={true}
          activeIcon={<CloseCall />}
          onClick={onDisconnect}
        />
      )}
    </TabBarContainer>
  );
};

export default MobileTabBar;

const VolumeIconCircle = styled.div`
  width: 100%;
  height: 100%;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  background: ${({ theme, $active }) =>
    $active
      ? theme.callComponent.controlButtonBackgroundActive
      : theme.callComponent.controlButtonBackgroundInactive};

  svg {
    fill: none !important;
    color: ${({ theme, $active }) =>
      $active
        ? theme.callComponent.controlButtonIconActive
        : theme.callComponent.controlButtonIconInactive} !important;
  }
`;

const TabBarContainer = styled.div`
  display: flex;
  align-items: start;
  justify-content: space-evenly;
  width: 100%;
  padding: 8px 4px calc(16px + env(safe-area-inset-bottom, 0px));
  min-height: 72px;
  flex-shrink: 0;
  background: ${({ theme }) => theme.menu.background};
  border-top: 1px solid ${({ theme }) => theme.sidebar.borderColor};
`;

const TabButtonWrapper = styled.div`
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const TabBadge = styled.div`
  position: absolute;
  top: 4px;
  right: 4px;
  width: 12px;
  height: 12px;
  background: #FF3B30;
  border-radius: 50%;
  border: 2px solid ${({ theme }) => theme.menu.background};
  pointer-events: none;
`;
